import React from "react";
import { TextField } from "@mui/material";
import type { Job } from "../../../../../../models";
import { getValidationErrorKey } from "../../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../../admin/ImportSettings";
import { ColumnContext } from "../standard-column-types";
import { useSalespersonPhoneColumnViewModel } from "./SalespersonPhone.column.vm";
import { useValidationErrors } from "../../shared/hooks/ValidationErrorsContext";

export interface SalespersonPhoneColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** Custom label override */
	label?: string;
}

/**
 * Mobile-friendly SalespersonPhone column component.
 * Shows phone number for selected salesperson.
 * Editable only for new salespersons.
 */
export const SalespersonPhoneColumnMobile: React.FC<SalespersonPhoneColumnMobileProps> = ({
	job,
	columnContext,
	label,
}) => {
	const vm = useSalespersonPhoneColumnViewModel(columnContext);
	const validationErrors = useValidationErrors();

	const value = vm.getPhoneValue(job);
	const isEditable = vm.getIsEditable(job);

	const errorKey = getValidationErrorKey("salespersonMobilePhone", job.id);
	const hasError = isEditable && !!validationErrors?.[errorKey];

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (!isEditable) return;
		vm.handleChange(job.id, event.target.value);
	};

	return (
		<TextField
			size="small"
			fullWidth
			type="tel"
			label={label || columnDisplayNames.salespersonMobilePhone}
			value={value}
			disabled={!isEditable}
			error={hasError}
			helperText={hasError ? validationErrors[errorKey] : undefined}
			onChange={handleChange}
			placeholder={isEditable ? "Enter phone number" : ""}
			InputProps={{
				readOnly: !isEditable,
			}}
		/>
	);
};
